import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white font-sans relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-cyan-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 p-4 sm:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg sm:text-xl text-gray-300">Last updated: November 2, 2025</p>
        </div>

        {/* Introduction */}
        <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-white/20 shadow-2xl">
          <p className="text-gray-300 leading-relaxed text-base sm:text-lg">
            At Fixet, we are committed to protecting your privacy and ensuring the security of your personal information.
            This Privacy Policy explains how we collect, use, and safeguard your data when you use our urban services platform.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6 sm:space-y-8">
          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-cyan-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">📊</span> Information We Collect
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>We collect information you provide directly to us, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Account information (name, email, phone number, profile picture)</li>
                <li>Location data for service matching and delivery tracking</li>
                <li>Payment information for transactions</li>
                <li>Communication records with our support team</li>
                <li>Service preferences and usage history</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-green-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🎯</span> How We Use Your Information
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>Your information helps us provide and improve our services:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Connect you with nearby service providers</li>
                <li>Process payments and maintain transaction records</li>
                <li>Provide real-time tracking and updates</li>
                <li>Send important notifications and alerts</li>
                <li>Improve our platform based on usage patterns</li>
                <li>Ensure safety and security for all users</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-yellow-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🔒</span> Information Sharing & Security
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p className="font-semibold text-white">We do not sell your personal information to third parties.</p>
              <p>We may share information only in these circumstances:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>With service providers to complete your requests</li>
                <li>With payment processors for transaction processing</li>
                <li>When required by law or to protect user safety</li>
                <li>With your explicit consent</li>
              </ul>
              <p className="mt-4 font-semibold text-white">Data Security Measures:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>End-to-end encryption for all communications</li>
                <li>Secure payment processing with PCI compliance</li>
                <li>Regular security audits and updates</li>
                <li>Strict access controls for employee data</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-purple-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🍪</span> Cookies & Tracking
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>We use cookies and similar technologies to enhance your experience:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Essential cookies for app functionality</li>
                <li>Analytics cookies to improve our services</li>
                <li>Location tracking for service matching</li>
                <li>Preference cookies to remember your settings</li>
              </ul>
              <p>You can control cookie preferences in your app settings.</p>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-blue-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">📞</span> Your Rights & Contact
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p className="font-semibold text-white">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access and review your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request data deletion</li>
                <li>Opt-out of marketing communications</li>
                <li>Data portability</li>
              </ul>
              <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-white/5 rounded-xl sm:rounded-2xl border border-white/10">
                <p className="font-semibold text-cyan-400 mb-2">Contact Us</p>
                <p>If you have questions about this Privacy Policy or want to exercise your rights:</p>
                <p className="mt-2 text-sm sm:text-base">📧 privacy@fixet.com</p>
                <p className="text-sm sm:text-base">📱 +91-XXXXXXXXXX</p>
                <p className="text-sm sm:text-base">🏢 Fixet Technologies Pvt Ltd, New Delhi, India</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-12 p-4 sm:p-6 backdrop-blur-lg bg-white/5 rounded-xl sm:rounded-2xl border border-white/10">
          <p className="text-gray-400 text-sm sm:text-base">
            This privacy policy is effective as of November 2, 2025. We may update this policy periodically.
            Continued use of our services constitutes acceptance of any changes.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;